// CompositeRequestsPage.js
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, fetchNewMaterialCategories } from '../services/api';
import { auth } from '../firebaseConfig';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import './PageLayout.css';

// ─── Success Modal ──────────────────────────────────────────────────────────
const SuccessModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;
  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header">
          <div className="success-modal-icon">{icon || '✅'}</div>
          <h2>{title || 'Success!'}</h2>
        </div>
        <div className="success-modal-body"><p>{message}</p></div>
        <div className="success-modal-footer">
          <button className="success-modal-button" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
};

// ─── Category Tabs ──────────────────────────────────────────────────────────
const TABS = [
  { key: 'pending',   label: 'Pending',   icon: '🕐' },
  { key: 'approved',  label: 'Approved',  icon: '✅' },
  { key: 'rejected',  label: 'Rejected',  icon: '❌' },
  { key: 'dismissed', label: 'Dismissed', icon: '🗑️' },
  { key: 'deletionRequested', label: 'Request Deletion', icon: '🚨' },
];

const CategoryTabs = ({ counts, activeTab, onChange }) => (
  <div className="cr-tabs">
    {TABS.map(({ key, label, icon }) => {
      const count = counts[key] ?? 0;
      const isActive = activeTab === key;
      return (
        <button
          key={key}
          className={`cr-tab${isActive ? ' cr-tab--active' : ''}`}
          onClick={() => onChange(key)}
        >
          <span className="cr-tab__icon">{icon}</span>
          <span className="cr-tab__label">{label}</span>
          {count > 0 && (
            <span className={`cr-tab__badge${isActive ? ' cr-tab__badge--active' : ''}`}>
              {count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

// ─── Helpers ────────────────────────────────────────────────────────────────
const groupLineItemsWithHeadings = (items) => {
  const result = [];
  let currentHeading = null;
  items.forEach((item, index) => {
    const itemHeading = item.heading || null;
    if (itemHeading !== currentHeading) {
      if (itemHeading) result.push({ id: `heading-${index}-${itemHeading}`, heading: itemHeading, isHeading: true });
      currentHeading = itemHeading;
    }
    result.push(item);
  });
  return result;
};

const fmtDate = (d) => {
  if (!d) return '-';
  // MySQL datetime strings have no 'Z' suffix — JS treats them as local time by default.
  // Explicitly mark as UTC so the Sydney conversion is correct.
  const utcDate = typeof d === 'string' && !d.endsWith('Z') && !d.includes('+')
    ? new Date(d.replace(' ', 'T') + 'Z')   // "2026-05-14 00:27:07" → "2026-05-14T00:27:07Z"
    : new Date(d);
  return utcDate.toLocaleString('en-AU', {
    timeZone: 'Australia/Sydney',
    day:    '2-digit',
    month:  '2-digit',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};
const metricIcon = (k) =>
  k === 'carbon' ? '🌡️' : k === 'biogenic' ? '🌱' : k === 'weight' ? '⚖️' : k === 'cost' ? '💰' : '📊';

const metricFromItem = (li) => {
  const m = String(li?.measurementId || li?.code || '').toLowerCase();
  if (m.includes('rate')) return 'cost';
  if (m.includes('biogenic')) return 'biogenic';
  if (m.includes('weight')) return 'weight';
  if (m.includes('co2')) return 'carbon';
  return 'other';
};

const getDisplayName = (r) => {
  const md = r?.MaterialData || {};
  return md.BaseMaterialName || md.OriginalMaterialName || md.SearchedMaterialName ||
    md.RequestedName || md.Name || '(unnamed composite)';
};

const getComments = (r) => r?.UserComments || r?.EnvironmentalData?._userComments || '';

// ─── Token cache (module-level) ──────────────────────────────────────────────
let cachedToken = null;
let tokenExpiry  = 0;
let bypassToken  = null;

const getToken = async () => {
  const now = Date.now();
  if (cachedToken && now < tokenExpiry) return cachedToken;
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Not authenticated. Please log in.');
  cachedToken = token;
  tokenExpiry  = now + 5 * 60 * 1000;
  return token;
};

const fetchJSON = async (url, opts = {}) => {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...opts.headers,
    };
    if (bypassToken) headers['X-Rate-Limit-Bypass'] = bypassToken;

    const res = await fetch(url, { ...opts, headers });

    if (res.status === 429) {
      const errorData = await res.json();
      const error = new Error(errorData.message || 'Rate limited');
      error.status    = 429;
      error.canBypass = errorData.canBypass;
      throw error;
    }

    if (res.status === 401 && cachedToken) {
      cachedToken = null;
      tokenExpiry  = 0;
      const freshToken = await getToken();
      const retryRes = await fetch(url, {
        ...opts,
        headers: { ...headers, 'Authorization': `Bearer ${freshToken}` },
      });
      if (!retryRes.ok) throw new Error((await retryRes.text()) || `HTTP ${retryRes.status}`);
      return retryRes.json();
    }

    if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
    return res.json();
  } catch (error) {
    console.error('❌ Fetch error:', error);
    throw error;
  }
};

// ─── Component ───────────────────────────────────────────────────────────────
export default function CompositeRequestsPage() {
  const navigate = useNavigate();
  const { user }          = useContext(AuthContext);
  const { selectedRegion } = useRegion();
  const role = user?.role || 'user';

  // Categorised buckets – only "pending" is populated from the server;
  // approved / rejected / dismissed accumulate locally for this session.
  const [categorized, setCategorized] = useState({
    pending:   [],
    approved:  [],
    rejected:  [],
    dismissed: [],
    deletionRequested: [],
  });
  const [activeTab, setActiveTab] = useState('pending');

  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState('');
  const [notesById, setNotesById] = useState({});

  // Detail modal
  const [selected, setSelected]     = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Processing + Success modal
  const [processingId, setProcessingId]       = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({ title: '', message: '', icon: '✅' });
  const [approveConfirm, setApproveConfirm] = useState({ open: false, item: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null });


  // Rate-limit bypass
  const [showBypassModal, setShowBypassModal] = useState(false);

  // Category id → name map so the detail panel shows the readable name, not the UUID.
  const [categoryMap, setCategoryMap] = useState({});
  const resolveCategory = useCallback(
    (cat) => (cat ? (categoryMap[cat] || cat) : ''),
    [categoryMap],
  );
  const [bypassPassword, setBypassPassword]   = useState('');
  const [bypassError, setBypassError]         = useState('');
  const [bypassLoading, setBypassLoading]     = useState(false);
  const [pendingRetry, setPendingRetry]       = useState(null);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const showSuccess = (title, message, icon = '✅') => {
    setSuccessModalData({ title, message, icon });
    setShowSuccessModal(true);
  };

  /** Move an item from one bucket to another */
  const moveItem = (id, from, to) => {
    setCategorized((prev) => {
      const item = prev[from].find((r) => r.RequestId === id);
      if (!item) return prev;
      return {
        ...prev,
        [from]: prev[from].filter((r) => r.RequestId !== id),
        [to]:   [...prev[to], item],
      };
    });
  };

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const commonHeaders = {
        'X-User-Role':  role,
        'X-User-Id':    user?.uid   || 'unknown',
        'X-User-Email': user?.email || 'unknown',
        'x-region':     selectedRegion || 'vquest',
      };
 
      const regions = await fetchJSON(`${API_BASE_URL}/regions`, { headers: commonHeaders });
      const regionNameMap = {};
      (Array.isArray(regions) ? regions : []).forEach((r) => { regionNameMap[r.id] = r.name; });
      const selectedRegionName = regionNameMap[selectedRegion] || selectedRegion;
 
      // Fetch ALL statuses in one call, then split by Status
      const data = await fetchJSON(`${API_BASE_URL}/composite-requests/all`, { headers: commonHeaders });
      const allRequests = Array.isArray(data?.requests) ? data.requests : [];
 
      // Filter to current region first
      const forRegion = allRequests.filter(
        (r) => r.RegionName?.toLowerCase() === selectedRegionName.toLowerCase(),
      );
 
      // Split into buckets by DB Status
      setCategorized({
        pending:   forRegion.filter(r => r.Status === 'Pending'),
        approved:  forRegion.filter(r => r.Status === 'Approved'),
        rejected:  forRegion.filter(r => r.Status === 'Rejected'),
        dismissed: forRegion.filter(r => r.Status === 'Dismissed'),
        deletionRequested: forRegion.filter(r => r.Status === 'DeletionRequested'),
      });
 
    } catch (e) {
      if (e.status === 429) {
        setPendingRetry(() => load);
        setShowBypassModal(true);
        setErr('Rate limited — please verify your identity to continue.');
      } else {
        setErr(String(e.message || e));
      }
      setCategorized({ pending: [], approved: [], rejected: [], dismissed: [], deletionRequested: [] });
    } finally {
      setLoading(false);
    }
  }, [user, selectedRegion, role]);

  useEffect(() => {
    if (user && selectedRegion) load();
  }, [user, selectedRegion, load]);

  // Load category names for the selected region (id → name).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await fetchNewMaterialCategories();
        if (cancelled) return;
        const map = {};
        (Array.isArray(cats) ? cats : []).forEach((c) => {
          if (c?.id) map[c.id] = c.name || c.id;
        });
        setCategoryMap(map);
      } catch (e) {
        console.error('❌ Error loading category names:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRegion]);

  useEffect(() => {
    if (showDetail) document.body.classList.add('modal-open');
    else            document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [showDetail]);

  // ── Email helper ───────────────────────────────────────────────────────────
  const sendCompositeApprovalEmail = async (composite) => {
    try {
      const requestData = {
        submitterEmail:      composite.SubmittedByEmail,
        submitterName:       composite.SubmittedByEmail?.split('@')[0] || 'User',
        submitterRole:       composite.SubmittedByRole || 'user',
        compositeName:       getDisplayName(composite),
        compositeDescription: composite.MaterialData?.Description || 'No description provided',
        regionName:          composite.RegionName || 'Unknown Region',
        requestId:           composite.RequestId,
        submittedDate:       composite.SubmittedAt ? new Date(composite.SubmittedAt).toLocaleDateString() : 'Unknown',
        approvedDate:        new Date().toLocaleDateString(),
        adminName:           user?.email || 'Admin Team',
        userComments:        getComments(composite) || '',
        environmentalSummary: composite.EnvironmentalData || {},
      };
      const result = await fetchJSON(`${API_BASE_URL}/email/send-composite-approval`, {
        method: 'POST',
        headers: {
          'X-User-Role':  role,
          'X-User-Id':    user?.uid   || 'unknown',
          'X-User-Email': user?.email || 'unknown',
          'x-region':     selectedRegion || 'vquest',
        },
        body: JSON.stringify(requestData),
      });
      return !!result.success;
    } catch {
      return false;
    }
  };

  // ── Actions ────────────────────────────────────────────────────────────────

  /** Approve: send email + move to approved bucket */
    const approve = async (id) => {
    try {
      setProcessingId(id);
      const composite = selected?.RequestId === id
        ? selected
        : categorized.pending.find((c) => c.RequestId === id);

      // 1. Mark as Approved in DB (status only — no material insertion)
      await fetchJSON(`${API_BASE_URL}/composite-requests/${id}/approve`, {
        method: 'POST',
        headers: {
          'X-User-Role':  role,
          'X-User-Id':    user?.uid   || 'unknown',
          'X-User-Email': user?.email || 'unknown',
          'x-region':     selectedRegion || 'vquest',
        },
      });

      // 2. Send approval email
      let emailSent = false;
      if (composite?.SubmittedByEmail) {
        emailSent = await sendCompositeApprovalEmail(composite);
      }

      moveItem(id, 'pending', 'approved');
      setShowDetail(false);
      setSelected(null);

      showSuccess(
        emailSent ? 'Approval Sent' : 'Approved (Email Failed)',
        `"${getDisplayName(composite)}" marked as approved.${emailSent ? ' Notification email sent to submitter.' : ' Email could not be delivered.'}`,
        emailSent ? '✅' : '⚠️',
      );
    } catch (e) {
      if (e.status === 429) {
        setPendingRetry(() => () => approve(id));
        setShowBypassModal(true);
        setErr('Rate limited — please verify your identity to continue.');
      } else {
        showSuccess('Approval Error', `Approval failed: ${e.message}`, '❌');
      }
    } finally {
      setProcessingId(null);
    }
  };

  const confirmApprove = async () => {
    const item = approveConfirm.item;
    if (!item) return;

    setApproveConfirm({ open: false, item: null });
    await approve(item.RequestId);
  };

  // ── Rejection email helper ─────────────────────────────────────────────────
  const sendCompositeRejectionEmail = async (composite, reviewNotes) => {
    try {
      const requestData = {
        submitterEmail:       composite.SubmittedByEmail,
        submitterName:        composite.SubmittedByEmail?.split('@')[0] || 'User',
        submitterRole:        composite.SubmittedByRole || 'user',
        compositeName:        getDisplayName(composite),
        compositeDescription: composite.MaterialData?.Description || 'No description provided',
        regionName:           composite.RegionName || 'Unknown Region',
        requestId:            composite.RequestId,
        submittedDate:        composite.SubmittedAt
                                ? new Date(composite.SubmittedAt).toLocaleDateString()
                                : 'Unknown',
        rejectedDate:         new Date().toLocaleDateString(),
        adminName:            user?.email || 'Admin Team',
        userComments:         getComments(composite) || '',
        reviewNotes:          reviewNotes || '',
        environmentalSummary: composite.EnvironmentalData || {},
      };

      const result = await fetchJSON(`${API_BASE_URL}/email/send-composite-rejection`, {
        method: 'POST',
        headers: {
          'X-User-Role':  role,
          'X-User-Id':    user?.uid   || 'unknown',
          'X-User-Email': user?.email || 'unknown',
          'x-region':     selectedRegion || 'vquest',
        },
        body: JSON.stringify(requestData),
      });

      return !!result.success;
    } catch (error) {
      console.error('❌ Error sending composite rejection email:', error);
      return false;
    }
  };

  /** Reject: API call + rejection email + move to rejected bucket */
  const reject = async (id) => {
    const notes = (notesById[id] || '').trim();
    if (!notes) {
      showSuccess('Notes Required', 'Please enter admin notes explaining the reason for rejection.', '📝');
      return;
    }
    try {
      const composite     = selected?.RequestId === id ? selected : categorized.pending.find((c) => c.RequestId === id);
      const compositeName = getDisplayName(composite) || 'Composite';

      await fetchJSON(`${API_BASE_URL}/composite-requests/${id}/reject`, {
        method: 'POST',
        headers: {
          'X-User-Role':  role,
          'X-User-Id':    user?.uid   || 'unknown',
          'X-User-Email': user?.email || 'unknown',
          'x-region':     selectedRegion || 'vquest',
        },
        body: JSON.stringify({ reviewNotes: notes }),
      });

      let emailSent = false;
      if (composite?.SubmittedByEmail) {
        emailSent = await sendCompositeRejectionEmail(composite, notes);
      }

      moveItem(id, 'pending', 'rejected');
      setShowDetail(false);
      setSelected(null);
      showSuccess(
        emailSent ? 'Request Rejected' : 'Rejected (Email Failed)',
        `"${compositeName}" has been rejected.${emailSent ? ' Notification email sent to the submitter.' : ' Email notification could not be delivered.'}`,
        emailSent ? '🚫' : '⚠️',
      );
    } catch (e) {
      if (e.status === 429) {
        setPendingRetry(() => () => reject(id));
        setShowBypassModal(true);
        setErr('Rate limited — please verify your identity to continue.');
      } else {
        showSuccess('Rejection Failed', `Reject failed: ${e.message}`, '❌');
      }
    }
  };

  /** Dismiss: move to dismissed bucket (local only) */
  const dismiss = async (id) => {
      try {
        await fetchJSON(`${API_BASE_URL}/composite-requests/${id}/dismiss`, {
          method: 'POST',
          headers: {
            'X-User-Role':  role,
            'X-User-Id':    user?.uid   || 'unknown',
            'X-User-Email': user?.email || 'unknown',
            'x-region':     selectedRegion || 'vquest',
          },
        });
  
        moveItem(id, 'pending', 'dismissed');
  
        if (selected?.RequestId === id) {
          setShowDetail(false);
          setSelected(null);
        }
      } catch (e) {
        showSuccess('Dismiss Failed', `Could not dismiss request: ${e.message}`, '❌');
      }
    };

  // ── Rate-limit bypass ──────────────────────────────────────────────────────
  const handleBypass = async () => {
    if (!bypassPassword.trim()) { setBypassError('Please enter your password.'); return; }
    setBypassLoading(true);
    setBypassError('');
    try {
      const token = await getToken();
      const res   = await fetch(`${API_BASE_URL}/admin/bypass-rate-limit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password: bypassPassword }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setBypassError(data.error || 'Invalid password'); return; }
      bypassToken = data.bypassToken;
      setShowBypassModal(false);
      setBypassPassword('');
      setBypassError('');
      setErr('');
      if (pendingRetry) { pendingRetry(); setPendingRetry(null); }
    } catch {
      setBypassError('Failed to verify password. Please try again.');
    } finally {
      setBypassLoading(false);
    }
  };

  const deleteRequestedMaterial = async (id) => {
    setDeleteConfirm({ open: false, item: null });

    try {
      await fetchJSON(`${API_BASE_URL}/composite-requests/${id}/delete-approved-material`, {
        method: 'DELETE',
        headers: {
          'X-User-Role': role,
          'X-User-Id': user?.uid || 'unknown',
          'X-User-Email': user?.email || 'unknown',
          'x-region': selectedRegion || 'vquest',
        },
      });

      setCategorized(prev => ({
        ...prev,
        deletionRequested: prev.deletionRequested.filter(r => r.RequestId !== id),
      }));

      setShowDetail(false);
      setSelected(null);

      showSuccess('Material Deleted', 'The private material has been removed from the database.', '🗑️');
    } catch (e) {
      showSuccess('Delete Failed', e.message, '❌');
    }
  };

  // ── Prefill helpers ────────────────────────────────────────────────────────
  const buildMaterialPrefill = (req) => {
    const md  = req?.MaterialData    || {};
    const env = req?.EnvironmentalData || {};
    return {
      Name:           md.RequestedName || getDisplayName(req),
      Description:    md.Description   || `Created from user composite request (${req.RegionName || ''})`,
      ColorHex:       md.ColorHex      || '#000000',
      QuantityType:   md.QuantityType  || '',
      CategoryId:     md.CategoryId    || '',
      Visibility:     req.Visibility   || 'public',
      OwnerEmail:     req.SubmittedByEmail || '',
      _fromCompositeRequestId: req.RequestId,
      _regionName:    req.RegionName,
      _envTotals:     env,
      _submitter:     { email: req.SubmittedByEmail, role: req.SubmittedByRole },
      _userComments:  getComments(req),
    };
  };

  const goToNewMaterial = (req) => {
    localStorage.setItem('newCompositePrefill', JSON.stringify(buildMaterialPrefill(req)));
    navigate('/newmaterial', { state: { prefill: buildMaterialPrefill(req) } });
  };

  const goToNewBreakdown = () => navigate('/newbreakdown');

  const isAdjusted = (li) => {
    if (!li) return false;
    if (li.user_added || li.isUserAdded || li.isUserAdjusted || li._source === 'user') return true;
    return Array.isArray(li.line_item_rows) && li.line_item_rows.length > 0;
  };

  // ── Counts for tab badges ──────────────────────────────────────────────────
  const counts = {
    pending:   categorized.pending.length,
    approved:  categorized.approved.length,
    rejected:  categorized.rejected.length,
    dismissed: categorized.dismissed.length,
    deletionRequested: categorized.deletionRequested.length,
  };

  const visibleRequests = categorized[activeTab] || [];

  // ── Empty-state copy per tab ───────────────────────────────────────────────
  const emptyMessages = {
    pending:   { emoji: '🎉', title: 'All caught up!', body: `No pending requests for region: ${selectedRegion}` },
    approved:  { emoji: '✅', title: 'No approvals yet', body: 'Approved requests will appear here.' },
    rejected:  { emoji: '❌', title: 'No rejections yet', body: 'Rejected requests will appear here.' },
    dismissed: { emoji: '🗑️', title: 'Nothing dismissed', body: 'Dismissed requests will appear here.' },
    deletionRequested: { emoji: '🚨', title: 'No deletion requests', body: 'Private material deletion requests will appear here.'},
  };

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!user) return (
    <div className="page-container">
      <div className="loading"><div className="spinner" /> Authenticating…</div>
    </div>
  );

  if (!selectedRegion) return (
    <div className="page-container">
      <div className="loading"><div className="spinner" /> Loading region…</div>
    </div>
  );

  if (role !== 'admin') return (
    <div className="page-container">
      <div className="form-section" style={{ textAlign: 'center' }}>
        <h2>Access Restricted</h2>
        <p>This page is only available to administrators.</p>
        <p>Your role: <b>{role}</b></p>
      </div>
    </div>
  );

  // ── Shared card actions renderer ───────────────────────────────────────────
  const renderCardActions = (r, compact = false) => {
    const id = r.RequestId;
    return (
      <div className="line-item-actions" style={compact ? { justifyContent: 'center' } : {}}>
        {activeTab === 'pending' && (
          <>
            <button className="btn btn--primary" onClick={() => goToNewMaterial(r)}>
              ➕ New Material (prefilled)
            </button>
            <button className="btn btn--primary" onClick={() => goToNewBreakdown(r)}>
              🧩 New Breakdown
            </button>
            <button
              className="btn btn--primary"
              onClick={() => setApproveConfirm({ open: true, item: r })}
              disabled={processingId === id}
              style={{ backgroundColor: '#4A9B8F', color: 'white', opacity: processingId === id ? 0.7 : 1 }}
              title="Mark as approved and notify submitter by email"
            >
              {processingId === id ? '⏳ Sending…' : '✅ Approve (Notify Only)'}
            </button>
            <button className="btn btn--secondary" onClick={() => reject(id)}>
              ❌ Reject
            </button>
            <button className="btn btn--secondary" onClick={() => dismiss(id)}>
              🗑️ Dismiss
            </button>
          </>
        )}
        {activeTab !== 'pending' && (
          <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: 14 }}>
            {activeTab === 'approved'  && '✅ Approved'}
            {activeTab === 'rejected'  && '❌ Rejected'}
            {activeTab === 'dismissed' && '🗑️ Dismissed'}
          </span>
        )}
        {activeTab === 'deletionRequested' && (
          <>
            <button
              className="btn btn--secondary"
              onClick={() => setDeleteConfirm({ open: true, item: r })}
              style={{ backgroundColor: '#b91c1c', color: 'white' }}
            >
              🗑️ Delete Material
            </button>
          </>
        )}
        {compact && (
          <button className="btn btn--ghost" onClick={() => { setShowDetail(false); setSelected(null); }}>
            Close
          </button>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-title-section">
        <h1 className="cr-title">Material Requests</h1>
        <p className="page-description">
          Review material requests. Use the buttons to create a new material and fill its breakdown.
        </p>
        <div className="region-info">
          <span className="badge badge--region">Current Region: {selectedRegion}</span>
        </div>
      </div>

      {/* ── Category tabs ── */}
      <CategoryTabs counts={counts} activeTab={activeTab} onChange={setActiveTab} />

      {err && <div className="alert alert--error">⚠️ {err}</div>}

      {loading && activeTab === 'pending' && (
        <div className="loading"><div className="spinner" /> Loading…</div>
      )}

      {/* Empty state */}
      {(!loading || activeTab !== 'pending') && visibleRequests.length === 0 && (() => {
        const { emoji, title, body } = emptyMessages[activeTab];
        return (
          <div className="form-section" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>{emoji}</div>
            <h3>{title}</h3>
            <p>{body}</p>
          </div>
        );
      })()}

      {/* Request cards */}
      {visibleRequests.length > 0 && (
        <div className="cr-list">
          {visibleRequests.map((r) => {
            const env      = r.EnvironmentalData || {};
            const id       = r.RequestId;
            const comments = getComments(r);

            return (
              <div key={id} className="form-section cr-card">
                <div className="cr-card__top">
                  <div>
                    <h2 className="cr-card__title">{getDisplayName(r)}</h2>
                    <div className="region-info cr-badges">
                      <span className="badge badge--region">Region: {r.RegionName || '-'}</span>
                      <span className="badge badge--role">
                        Submitted by: {r.SubmittedByEmail} ({r.SubmittedByRole})
                      </span>
                      <span className="badge badge--date">Date: {fmtDate(r.SubmittedAt)}</span>
                      <span className="badge" style={{
                        backgroundColor: r.Visibility === 'private' ? '#92400e' : '#1e40af',
                        color: 'white',
                      }}>
                        {r.Visibility === 'private' ? '🔒 Private' : '🌐 Public'}
                      </span>
                      {r.IsResubmit === 1 && (
                      <span className="badge" style={{
                        backgroundColor: '#7c3aed',
                        color: 'white',
                        fontSize: 12,
                      }}>
                        🔄 Resubmit
                      </span>
                      )}
                      {/* 🆕 Update badge */}
                      {r.IsUpdate === 1 && (
                        <span className="badge" style={{ backgroundColor: '#1d4ed8', color: 'white', fontSize: 12 }}>
                          ✏️ Update to Existing Material
                        </span>
                      )}
                      {r.QuantityUnitId && (
                        <span className="badge" style={{
                          backgroundColor: '#374151',
                          color: 'white',
                          fontSize: 12,
                        }}>
                          QuantityUnitId: {r.QuantityUnitId}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    className="btn btn--primary"
                    onClick={() => { setSelected(r); setShowDetail(true); }}
                  >
                    Details
                  </button>
                </div>

                {/* Metrics chips */}
                <div className="cr-metrics">
                  {['carbon', 'biogenic', 'weight', 'cost'].map((k) => {
                    const d = env?.[k];
                    if (!d) return null;
                    return (
                      <div key={k} className="chip">
                        <span className="chip__icon">{metricIcon(k)}</span>
                        <span className="chip__label">{k.charAt(0).toUpperCase() + k.slice(1)}:</span>
                        <span className="chip__value">{d.value}</span>&nbsp;{r.MaterialData?.QuantityType || d.unit || ''}
                      </div>
                    );
                  })}
                </div>

                {/* Comments teaser */}
                {comments && (
                  <div className="cr-user-comments cr-user-comments--teaser">
                    <strong>🗒️ User comments:</strong>
                    <div className="cr-user-comments__text">
                      {comments.length > 180 ? `${comments.slice(0, 180)}…` : comments}
                    </div>
                  </div>
                )}

                {/* Admin notes (only on pending) */}
                {activeTab === 'pending' && (
                  <div className="cr-notes">
                    <label className="label">Admin Notes</label>
                    <textarea
                      rows={3}
                      className="input"
                      placeholder="Optional reason to send back to the submitter"
                      value={notesById[id] || ''}
                      onChange={(e) => setNotesById((prev) => ({ ...prev, [id]: e.target.value }))}
                    />
                  </div>
                )}

                {renderCardActions(r)}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Detail Modal ── */}
      {showDetail && selected && (
        <div
          className="cr-modal-overlay"
          onClick={() => { setShowDetail(false); setSelected(null); }}
        >
          <div
            className="cr-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cr-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal__header">
              <h2 id="cr-modal-title" className="modal__title">📋 {getDisplayName(selected)}</h2>
              <button className="btn btn--ghost" onClick={() => { setShowDetail(false); setSelected(null); }}>×</button>
            </div>

            {/* Meta grid */}
            <div className="cr-meta-grid">
              <div className="form-section cr-pane">
                <h3 className="cr-pane__title">🏗️ Material</h3>
                <p><b>Name:</b> {getDisplayName(selected)}</p>
                <p><b>Description:</b> {selected.MaterialData?.Description || '-'}</p>
                <p><b>Quantity Type:</b> {selected.MaterialData?.QuantityType || '-'}</p>
                <p><b>Category:</b> {resolveCategory(selected.MaterialData?.CategoryId) || '-'}</p>
                <p>
                  <b>Color:</b>{' '}
                  <span className="cr-color-pill" style={{ background: selected.MaterialData?.ColorHex || '#ccc' }}>
                    {selected.MaterialData?.ColorHex || '#ccc'}
                  </span>
                </p>
              </div>

              <div className="form-section cr-pane">
                <h3 className="cr-pane__title">👤 Submission</h3>
                <p><b>Request ID:</b> {selected.RequestId}</p>
                <p><b>Submitted by:</b> {selected.SubmittedByEmail} ({selected.SubmittedByRole})</p>
                <p><b>Region:</b> {selected.RegionName}</p>
                <p><b>Date:</b> {fmtDate(selected.SubmittedAt)}</p>
                <p><b>Status:</b> {selected.Status}</p>
                <p>
                  <b>Type:</b>{' '}
                  {selected.IsUpdate === 1 ? (
                    <>
                      <span style={{
                        background: '#dbeafe', color: '#1d4ed8',
                        padding: '2px 8px', borderRadius: 4, fontWeight: 'bold', fontSize: 13,
                      }}>
                        ✏️ Update to Existing Material
                      </span>
                      {selected.UpdateTargetId && (
                        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
                          Target ID: {selected.UpdateTargetId}
                        </span>
                      )}
                    </>
                  ) : selected.IsResubmit === 1 ? (
                    <span style={{
                      background: '#ede9fe', color: '#7c3aed',
                      padding: '2px 8px', borderRadius: 4, fontWeight: 'bold', fontSize: 13,
                    }}>
                      🔄 Resubmission
                    </span>
                  ) : (
                    <span style={{
                      background: '#dcfce7', color: '#166534',
                      padding: '2px 8px', borderRadius: 4, fontWeight: 'bold', fontSize: 13,
                    }}>
                      🆕 New Material
                    </span>
                  )}
                </p>
                <p><b>Visibility:</b>{' '}
                  <span style={{
                    background: selected.Visibility === 'private' ? '#fef3c7' : '#dbeafe',
                    color:      selected.Visibility === 'private' ? '#92400e' : '#1e40af',
                    padding: '2px 8px', borderRadius: 4, fontWeight: 'bold', fontSize: 13,
                  }}>
                    {selected.Visibility === 'private' ? '🔒 Private' : '🌐 Public'}
                  </span>
                </p>
                <p><b>Email:</b>{' '}
                  {selected.SubmittedByEmail
                    ? `✅ Will notify ${selected.SubmittedByEmail}`
                    : '❌ No email available'}
                </p>
              </div>
            </div>         
            {/* User comments */}
            {getComments(selected) && (
              <div className="form-section">
                <h3 className="cr-pane__title">🗒️ User Comments</h3>
                <div className="cr-user-comments">{getComments(selected)}</div>
              </div>
            )}

            {/* Environmental summary */}
            <div className="form-section">
              <h3 className="cr-pane__title">🌍 Environmental Summary</h3>
              <div className="cr-metrics cr-metrics--grid">
                {['carbon', 'biogenic', 'weight', 'cost'].map((k) => {
                  const d = selected.EnvironmentalData?.[k];
                  if (!d) return null;
                  return (
                    <div key={k} className="chip chip--card">
                      <span className="chip__icon">{metricIcon(k)}</span>
                      <div className="chip__stack">
                        <span className="chip__label">{k.charAt(0).toUpperCase() + k.slice(1)}</span>
                        <span className="chip__value">{d.value} {selected.MaterialData?.QuantityType || d.unit || ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Line items */}
            <div className="form-section">
              <h3 className="cr-pane__title">
                📦 Line Items <span className="cr-muted">(⭐ indicates user-adjusted)</span>
              </h3>
              <div className="table-scroll">
                <table className="cr-table">
                  <thead>
                    <tr>
                      <th>Description</th><th>Type</th><th>Unit</th>
                      <th className="num">Value</th><th className="num">Quantity</th><th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupLineItemsWithHeadings(selected.LineItemsData || []).map((item) => {
                      if (item.isHeading) {
                        return (
                          <tr key={item.id} className="heading-row">
                            <td colSpan="6" style={{
                              backgroundColor: '#f0f4f8', borderLeft: '4px solid #4a90e2',
                              padding: '12px 16px', fontWeight: 'bold', fontSize: 14, color: '#2c3e50',
                            }}>
                              📋 {item.heading}
                            </td>
                          </tr>
                        );
                      }
                      const type     = metricFromItem(item);
                      const adjusted = isAdjusted(item);
                      return (
                        <tr
                          key={item.id}
                          className={adjusted ? 'row--adjusted' : undefined}
                          title={adjusted ? 'Adjusted via Quantity Calculator / user-added' : undefined}
                        >
                          <td>{adjusted ? '⭐ ' : ''}{item.description}</td>
                          <td>{metricIcon(type)} {type.charAt(0).toUpperCase() + type.slice(1)}</td>
                          <td>{item.unit || ''}</td>
                          <td className="num">{item.value}</td>
                          <td className="num">{item.quantity}</td>
                          <td className="num">{item.total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Admin notes + actions in modal */}
            <div className="form-section" style={{ textAlign: 'center' }}>
              {activeTab === 'pending' && (
                <>
                  <label className="label">Admin Notes (for rejection)</label>
                  <textarea
                    rows={3}
                    className="input"
                    placeholder="Optional reason to send back to the submitter"
                    value={notesById[selected.RequestId] || ''}
                    onChange={(e) =>
                      setNotesById((prev) => ({ ...prev, [selected.RequestId]: e.target.value }))
                    }
                  />
                </>
              )}
              {renderCardActions(selected, true)}
            </div>
          </div>
        </div>
      )}

      {/* ── Rate-limit bypass modal ── */}
      {showBypassModal && (
        <div className="success-modal-overlay" onClick={() => setShowBypassModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-modal-header">
              <div className="success-modal-icon">🔐</div>
              <h2>Rate Limit Reached</h2>
            </div>
            <div className="success-modal-body">
              <p>Too many requests. Enter your password to continue:</p>
              <input
                type="password"
                className="input"
                placeholder="Enter your password"
                value={bypassPassword}
                onChange={(e) => setBypassPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleBypass()}
                style={{ marginTop: 12, width: '100%' }}
                autoFocus
              />
              {bypassError && (
                <p style={{ color: '#e74c3c', marginTop: 8, fontSize: 14 }}>{bypassError}</p>
              )}
            </div>
            <div className="success-modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                className="btn btn--secondary"
                onClick={() => { setShowBypassModal(false); setBypassPassword(''); setBypassError(''); }}
              >
                Cancel
              </button>
              <button className="success-modal-button" onClick={handleBypass} disabled={bypassLoading}>
                {bypassLoading ? 'Verifying…' : 'Verify & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {approveConfirm.open && approveConfirm.item && (
        <div className="success-modal-overlay" onClick={() => setApproveConfirm({ open: false, item: null })}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-modal-header">
              <div className="success-modal-icon">
                {approveConfirm.item.IsUpdate === 1 ? '✏️' : '✅'}
              </div>
              <h2>
                {approveConfirm.item.IsUpdate === 1
                  ? 'Approve Material Update?'
                  : 'Approve Material Request?'}
              </h2>
            </div>

            <div className="success-modal-body">
              {approveConfirm.item.IsUpdate === 1 ? (
                <p>
                  This will <strong>update the existing approved material</strong> in the database
                  and notify the submitter. The original material's ID will be preserved.
                </p>
              ) : (
                <p>
                  This will approve the new composite material request and notify the submitter by email.
                </p>
              )}
              <div style={{
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 8, padding: '10px 14px', fontSize: 13,
                color: '#374151', marginTop: 10,
              }}>
                <div><b>Material:</b> {approveConfirm.item.MaterialData?.Name || 'Unnamed'}</div>
                <div><b>Submitted by:</b> {approveConfirm.item.SubmittedByEmail}</div>
                <div><b>Region:</b> {approveConfirm.item.RegionName}</div>
                <div><b>Visibility:</b> {approveConfirm.item.Visibility === 'private' ? '🔒 Private' : '🌐 Public'}</div>
                {approveConfirm.item.IsUpdate === 1 && approveConfirm.item.UpdateTargetId && (
                  <div style={{ marginTop: 6, color: '#1d4ed8', fontWeight: 600 }}>
                    ✏️ Updating material ID: {approveConfirm.item.UpdateTargetId}
                  </div>
                )}
              </div>
            </div>

            <div className="success-modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                className="btn btn--secondary"
                onClick={() => setApproveConfirm({ open: false, item: null })}
              >
                Cancel
              </button>
              <button className="success-modal-button" onClick={confirmApprove}
                style={approveConfirm.item.IsUpdate === 1
                  ? { background: '#1d4ed8' }
                  : {}
                }
              >
                {approveConfirm.item.IsUpdate === 1 ? 'Yes, Apply Update' : 'Yes, Approve & Notify'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Delete modal ── */}
      {deleteConfirm.open && deleteConfirm.item && (
          <div className="success-modal-overlay" onClick={() => setDeleteConfirm({ open: false, item: null })}>
            <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="success-modal-header">
                <div className="success-modal-icon">🗑️</div>
                <h2>Delete Private Material?</h2>
              </div>

              <div className="success-modal-body">
                <p>
                  This will <strong>permanently delete this private material</strong> from the
                  database, including its breakdown factors and ownership record. This action
                  cannot be undone.
                </p>
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 8, padding: '10px 14px', fontSize: 13,
                  color: '#374151', marginTop: 10,
                }}>
                  <div><b>Material:</b> {getDisplayName(deleteConfirm.item)}</div>
                  <div><b>Submitted by:</b> {deleteConfirm.item.SubmittedByEmail}</div>
                  <div><b>Region:</b> {deleteConfirm.item.RegionName}</div>
                  {deleteConfirm.item.QuantityUnitId && (
                    <div style={{ marginTop: 6, color: '#b91c1c', fontWeight: 600 }}>
                      QuantityUnitId: {deleteConfirm.item.QuantityUnitId}
                    </div>
                  )}
                </div>
              </div>

              <div className="success-modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button
                  className="btn btn--secondary"
                  onClick={() => setDeleteConfirm({ open: false, item: null })}
                >
                  Cancel
                </button>
                <button
                  className="success-modal-button"
                  onClick={() => deleteRequestedMaterial(deleteConfirm.item.RequestId)}
                  style={{ background: '#b91c1c' }}
                >
                  Yes, Delete Material
                </button>
              </div>
            </div>
          </div>
        )}
      {/* ── Success modal ── */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={successModalData.title}
        message={successModalData.message}
        icon={successModalData.icon}
      />
    </div>
  );
}